import { useGetMachinesByLocation } from "@/machines/api/queries/machines.queries";
import { useMultiCameraStore } from "@/shared/stores/useMultiCameraStore";
import { Button } from "@/ui/components/ui/button";
import { Video } from "lucide-react";
import { useTranslation } from "react-i18next";

interface AddCameraAngleModalPayload {
  machineId: number;
  locationId: number;
  currentDeviceMxid: string;
}

type Props = {
  payload: AddCameraAngleModalPayload;
  onClose: () => void;
};

const AddCameraAngleModal = ({ payload, onClose }: Props) => {
  const { t } = useTranslation("machines");
  const { machineId, locationId, currentDeviceMxid } = payload;
  const { data: machines, isLoading } = useGetMachinesByLocation(locationId);
  const { addCamera, getCameras } = useMultiCameraStore();
  const existingCameras = getCameras(machineId);

  const availableMachines = machines?.data?.filter(
    (m) =>
      m.device_mxid !== currentDeviceMxid &&
      !existingCameras.includes(m.device_mxid)
  );

  const handleSelect = (deviceMxid: string) => {
    addCamera(machineId, deviceMxid);
    onClose();
  };

  return (
    <div className="flex flex-col gap-3 py-2">
      {isLoading ? (
        <p className="text-sm text-muted-foreground text-center py-4">
          {t("cameraAngleModal.loading")}
        </p>
      ) : !availableMachines || availableMachines.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">
          {t("cameraAngleModal.empty")}
        </p>
      ) : (
        availableMachines.map((machine) => (
          <Button
            key={machine.id}
            variant="outline"
            className="w-full justify-start gap-3 h-auto py-3"
            onClick={() => handleSelect(machine.device_mxid)}
          >
            <Video className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <div className="flex flex-col items-start">
              <span className="text-sm font-medium">{machine.name}</span>
              <span className="text-xs text-muted-foreground">
                {machine.device_mxid}
              </span>
            </div>
          </Button>
        ))
      )}
    </div>
  );
};

export default AddCameraAngleModal;
